import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import isPlainObject from 'lodash/isPlainObject';
import toSnakeCase from 'lodash/snakeCase';
import { compose } from 'recompose';
import CSSModules from 'react-css-modules';
import cloneDeep from 'lodash/cloneDeep';
import includes from 'lodash/includes';
import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import differenceWith from 'lodash/differenceWith';
import classNames from 'classnames';

import Button from '../../Button';
import EditBtn from '../../Button/EditBtn';
import Field from '../Field';
import ErrorBlock from '../../common/ErrorBlock';
import { COMMON } from '../../../constants/strings';
import { validateWithRule } from '../../../utils/validations';
import { FIELDS_TYPE } from '../../../constants';
import styles from '../index.styl';

const DEFAULT_EMPTY_VALUE = '-';

class Fieldset extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      edit: false,
      empty: !this.isNotEmpty(props.fieldset.fields),
      fields: props.fieldset.fields,
      initialFields: cloneDeep(props.fieldset.fields),
      errors: {},
      validationErrors: props.fieldset.errors || null,
    };
  }

  componentWillReceiveProps(nextProps) {
    const { fields, errors: validationErrors } = nextProps.fieldset;
    const { initialFields } = this.state;

    if (validationErrors) {
      this.setState({ validationErrors });
    }

    if (isEmpty(differenceWith(fields, initialFields, isEqual))) return;

    const empty = !this.isNotEmpty(fields);

    const state = {
      empty,
      fields,
      initialFields: cloneDeep(fields),
    };

    this.setState(state);
  }

  toggleEditMode = () => {
    const { edit } = this.state;
    const { onEditModeChange, onCancelCallback } = this.props;

    if (edit && onCancelCallback) {
      // toggle to read mode
      onCancelCallback();
    }
    this.setState({ edit: !edit });

    if (onEditModeChange) {
      onEditModeChange(!edit);
    }
  };

  // helper for edit view - defines whether value is described with multiple data objects
  isComplexValue = (type, value) => {
    if (includes({ ...FIELDS_TYPE, MEDICATION_SELECT: 'medication' }, type)) {
      return false;
    }

    return isArray(value) && isPlainObject(value[0]);
  };

  hasValue = value => {
    return value && value.length > 0;
  };

  // helper for correct layout items number shown in view/edit mode
  handleFields = (prev, id, index, array) => {
    const { disabled: fieldSetDisabled, canEdit } = this.props;
    const { edit } = this.state;

    const { value: fieldValue, src, fieldName, mode, hidden, type } = array[
      index
    ];

    const isViewMode = fieldSetDisabled ? true : !edit;
    const currentMode = !edit ? 'view' : 'edit';

    const isComplexField = this.isComplexValue(type, fieldValue);
    const isMultipleFields = isArray(array[index]);

    const empty = canEdit ? false : !this.hasValue(fieldValue || src);

    const simpleEmptyField = !isComplexField && empty;
    const complexEmptyField = isComplexField && !this.isNotEmpty(fieldValue);

    // Support only view or edit mode. Use mode: 'view' or 'edit in field config
    if ((mode && mode !== currentMode) || hidden) {
      return prev;
    }
    // skip field in view mode if it hasn't any value
    // and isn't required for view

    if (isMultipleFields) {
      prev.push(
        <div key={`multiple-${index}`} styleName="flexRow">
          {array[index].map(item => (
            <Field
              className={item.name}
              key={item.name}
              children={item}
              editMode={edit}
              defaultEmptyValue={canEdit ? DEFAULT_EMPTY_VALUE : null}
            />
          ))}
        </div>,
      );
    } else if (!(isViewMode && (simpleEmptyField || complexEmptyField))) {
      // for one complex item in view mode (eg User Name)
      // there should be several items in edit mode (eg First/Last/Middle Name)
      if (edit && isComplexField) {
        // field's value-object to view is represented with array of values-objects to edit
        fieldValue.forEach((item, i) => {
          prev.push(
            <Field
              fullWidth={item.fullWidth}
              className={item.fieldName}
              key={`field-complex-${i}`}
              children={item}
              editMode={edit}
              onChange={(value, isDirty, ...rest) =>
                this.handleChange(item.fieldName, value, isDirty, ...rest)
              }
              onBlur={() => this.handleChange(item.fieldName, null, true)}
              defaultEmptyValue={canEdit ? DEFAULT_EMPTY_VALUE : null}
            />,
          );
        });
      } else {
        // in all other cases (view and edit mode for non complex values)
        // there should be only one item
        prev.push(
          <Field
            className={fieldName}
            key={`field-${index}`}
            children={array[index]}
            editMode={edit}
            onChange={(value, isDirty, localErrors) =>
              this.handleChange(fieldName, value, isDirty, localErrors)
            }
            onBlur={(value, isDirty, localErrors) => {
              this.handleChange(fieldName, null, true, localErrors);
            }}
            defaultEmptyValue={canEdit ? DEFAULT_EMPTY_VALUE : null}
          />,
        );
      }
    }

    return prev;
  };

  handleChange = (key, val, isDirty, localErrors) => {
    const { fields, validationErrors } = this.state;
    const state = { ...this.state };
    const value = this.getValue(val);
    const field = this.getField(fields, key);

    if (validationErrors) {
      state.validationErrors = null;
    }

    if (field) {
      // We pass on blur value=null, so we can do validation but don't need to change value again
      if (value !== null) {
        field.value = value;
      }

      if (
        (field.validationRules && isDirty) ||
        field.type === FIELDS_TYPE.CUSTOM_TIME_PICKER ||
        field.type === FIELDS_TYPE.DATE
      ) {
        const errors = this.validateField(field);

        if (errors) {
          state.errors = Object.assign({}, state.errors, {
            [field.fieldName]: errors,
          });
        } else if (localErrors) {
          state.errors = Object.assign({}, state.errors, {
            [field.fieldName]: localErrors,
          });
        } else {
          delete state.errors[field.fieldName];
          state.errors = { ...state.errors };
        }
      }

      this.setState(state);
    }
  };

  getField = (fields, key) => {
    let field;

    fields.forEach(item => {
      if (item && isArray(item.value)) {
        const nestedField = this.getField(item.value, key);
        if (nestedField) {
          field = nestedField;
        }
      }

      if (item && item.fieldName === key) {
        field = item;
      }
    });

    return field;
  };

  getValue = val => {
    if (isArray(val)) {
      return val.map(item => item.value);
    }

    if (val instanceof Object) {
      if (val.nativeEvent instanceof Event) {
        return null;
      }

      return val.value || val;
    }
    return val;
  };

  // check if all of the fields inside current fieldset has no values
  // if at least one field has value => true
  isNotEmpty = nextFields => {
    const { fieldset } = this.props;

    const fields = nextFields || fieldset.fields;

    return fields.some(member => {
      const fieldValue = member.value || member.src;

      // check if complex value's fields are empty too
      // value = [ {value: '' || null || undefined}, ...] => false

      if (this.isComplexValue(member)) {
        return member.some(item => Boolean(item.value));
      }

      if (this.isComplexValue(fieldValue)) {
        return fieldValue.some(item => Boolean(item.value)) && !member.hidden;
      }
      return Boolean(fieldValue) && !member.hidden;
    });
  };

  updateSaveValues = fields => {
    return fields.map(item => {
      const { fieldName, value } = item;
      // if backend needed only ids
      if (fieldName && fieldName.includes('Ids')) {
        const updatedValues = value.map(valueItem => valueItem.id);
        return { ...item, value: updatedValues };
      }
      return item;
    });
  };

  handleSaveButtonClick = () => {
    const { fields, edit } = this.state;
    const { onSave, fieldset, onEditModeChange } = this.props;

    this.setState({
      initialFields: cloneDeep(fields),
      edit: !edit,
    });

    onSave(this.updateSaveValues(fields), fieldset.type);

    if (onEditModeChange) {
      onEditModeChange(false);
    }
  };

  handleCancelButtonClick = () => {
    const { initialFields, edit } = this.state;
    const { onCancelCallback } = this.props;

    this.setState({
      errors: [],
      fields: cloneDeep(initialFields),
      edit: !edit,
    });

    if (onCancelCallback) onCancelCallback();
  };

  validateField = field => {
    const {
      intl: { formatMessage },
    } = this.props;

    const { validationRules, value } = field;
    const validationRulesKeys = Object.keys(validationRules);

    if (!validationRulesKeys.length) return null;

    const errorMessages = validationRulesKeys.reduce((errors, rule) => {
      const error = validateWithRule(
        rule,
        validationRules[rule],
        value,
        formatMessage,
      );

      if (error) errors.push(error);

      return errors;
    }, []);

    return errorMessages.length ? errorMessages : null;
  };

  getErrors = () => {
    const { errors } = this.state;

    const errorKeys = Object.keys(errors);

    return (
      errorKeys.length &&
      errorKeys.reduce((error, item) => error.concat(errors[item]), [])
    );
  };

  isErrored = () => {
    const { errors } = this.state;

    return !!Object.keys(errors).length;
  };

  render() {
    const { fieldset, disabled } = this.props;
    const { fields, edit, empty, validationErrors } = this.state;

    const fieldSetClass = `user-profile-fieldset-${toSnakeCase(
      fieldset.title,
    )}`;

    return (
      <div
        styleName={classNames('fieldset', {
          'fieldset--filled': fieldset.fill,
        })}
        className={classNames(fieldSetClass, {
          tl: edit,
        })}
      >
        <div styleName="fieldsetTitleWrapper">
          <h3
            styleName={classNames('sectionTitle', {
              'sectionTitle--danger': fieldset.dangered,
            })}
            className="user-profile-fieldset-title"
          >
            {fieldset.title}
          </h3>
          {!disabled && (
            <div
              styleName="fieldsetAction"
              className="user-profile-edit-button"
            >
              <EditBtn onClick={() => this.toggleEditMode()} />
            </div>
          )}
        </div>
        <div
          styleName={classNames('fieldsetContent', {
            'fieldsetContent--withColumns': fieldset.columns,
          })}
        >
          <Choose>
            <When condition={empty && disabled}>
              <p styleName="fieldsetContent__empty" className="o-70">
                <FormattedMessage {...COMMON.emptySection} />
              </p>
            </When>
            <Otherwise>
              {fields && fields.reduce(this.handleFields, [])}
            </Otherwise>
          </Choose>
        </div>
        <div className="user-profile-errors">
          {edit && this.isErrored() && (
            <ErrorBlock
              errors={this.getErrors()}
              className="mt3"
              style={{ marginRight: 16 }}
            />
          )}
          {validationErrors && validationErrors.length > 0 && (
            <ErrorBlock
              errors={validationErrors}
              className="mt3"
              style={{ marginRight: 16 }}
            />
          )}
        </div>
        {edit && (
          <Fragment>
            <Button
              className="mt3 mr2 user-profile-save-button"
              raised
              color="primary"
              onClick={() => this.handleSaveButtonClick()}
              disabled={this.isErrored()}
            >
              <FormattedMessage {...COMMON.save} />
            </Button>
            <Button
              className="mt3 user-profile-cancel-button"
              raised
              color="accent"
              onClick={() => this.handleCancelButtonClick()}
            >
              <FormattedMessage {...COMMON.cancel} />
            </Button>
          </Fragment>
        )}
      </div>
    );
  }
}

Fieldset.propTypes = {
  fieldset: PropTypes.object,
  onSave: PropTypes.func,
  disabled: PropTypes.bool,
  onCancelCallback: PropTypes.func,
  intl: PropTypes.object,
  canEdit: PropTypes.bool,
  onEditModeChange: PropTypes.func,
};

export default compose(
  injectIntl,
  CSSModules(styles, { allowMultiple: true }),
)(Fieldset);



// WEBPACK FOOTER //
// ./src/components/ProfileLayout/Fieldset/index.jsx